import { useLang } from "@/lib/i18n";
import { Link } from "react-router-dom";
import { pagePath } from "@/content/seo";
import { DIRECTORY_BASE } from "@/constants/labelCountries";
import { Linkedin, Youtube } from "lucide-react";

const PILLARS = [
  ["country-guide", { fr: "Guide France de la directive EmpCo", de: "Länderleitfaden Deutschland", es: "Guía España de la Directiva", it: "Guida Italia alla Direttiva", nl: "Landengids Nederland", en: "Country guide" }],
  ["banned-terms", { fr: "Termes environnementaux interdits", de: "Verbotene Umweltbegriffe", es: "Términos ambientales prohibidos", it: "Termini ambientali vietati", nl: "Verboden milieutermen", en: "Banned environmental terms" }],
  ["carbon-neutral", { fr: "Neutralité carbone : allégation encadrée", de: "Klimaneutral: geregelte Aussage", es: "Neutralidad de carbono regulada", it: "Carbon neutral: claim regolato", nl: "Klimaatneutraal: geregelde claim", en: "Carbon neutral claims" }],
  ["sanctions", { fr: "Sanctions et contrôles", de: "Abmahnung und Sanktionen", es: "Sanciones y controles", it: "Sanzioni e istruttorie", nl: "Sancties en handhaving", en: "Sanctions and enforcement" }],
  ["ecommerce-checklist", { fr: "Checklist e-commerce", de: "E-Commerce-Checkliste", es: "Checklist e-commerce", it: "Checklist e-commerce", nl: "E-commerce checklist", en: "E-commerce checklist" }],
];

const SECTORS = [
  ["sector-cosmetics", { fr: "Cosmétique", de: "Kosmetik", es: "Cosmética", it: "Cosmetica", nl: "Cosmetica", en: "Cosmetics" }],
  ["sector-textile", { fr: "Textile", de: "Textil", es: "Textil", it: "Tessile", nl: "Textiel", en: "Textile" }],
  ["sector-food", { fr: "Agroalimentaire", de: "Lebensmittel", es: "Alimentación", it: "Alimentare", nl: "Levensmiddelen", en: "Food" }],
  ["sector-ecommerce", { fr: "E-commerce", de: "E-Commerce", es: "E-commerce", it: "E-commerce", nl: "E-commerce", en: "E-commerce" }],
];

export default function Footer() {
  const { t, lang } = useLang();
  const label = (map) => map[lang] || map.en;
  return (
    <footer className="border-t border-border mt-16 md:mt-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-10 sm:py-14 md:py-16 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-8 sm:gap-10 text-sm">
        <div className="sm:col-span-2 md:col-span-2 max-w-md">
          <img src="/brand/logo-full.png" alt="EmpCo Compliance — outil de conformité aux allégations environnementales (directive UE 2024/825)" className="h-16 sm:h-20 md:h-24 w-auto mb-4 sm:mb-5 -ml-2" />
          <p className="font-serif text-base sm:text-lg text-primary mb-3">{t("brand.baseline")}</p>
          <p className="text-muted-foreground leading-relaxed">{t("footer.disclaimer")}</p>
          <p className="font-mono text-[11px] text-muted-foreground mt-4">{t("footer.directive")}</p>
        </div>
        <div>
          <div className="eyebrow mb-4">{t("footer.product")}</div>
          <ul className="space-y-2.5">
            <li><Link to={pagePath("product", lang)} className="hover:text-primary transition-colors" data-testid="footer-product-link">{t("footer.product")}</Link></li>
            <li><Link to={pagePath("pricing", lang)} className="hover:text-primary transition-colors" data-testid="footer-pricing-link">{t("nav.pricing")}</Link></li>
            <li><Link to={pagePath("freescan", lang)} className="hover:text-primary transition-colors" data-testid="footer-freescan-link">{t("hero.cta")}</Link></li>
            <li><Link to={pagePath("tool", lang)} className="hover:text-primary transition-colors" data-testid="footer-tool-link">{t("nav.method")}</Link></li>
            <li><Link to={pagePath("law", lang)} className="hover:text-primary transition-colors" data-testid="footer-directive-link">Directive UE 2024/825</Link></li>
            <li><Link to={pagePath("faq", lang)} className="hover:text-primary transition-colors" data-testid="footer-faq-link">FAQ</Link></li>
            <li><Link to={pagePath("guides", lang)} className="hover:text-primary transition-colors" data-testid="footer-guides-link">{t("nav.guides")}</Link></li>
            <li><Link to={DIRECTORY_BASE[lang] || DIRECTORY_BASE.en} className="hover:text-primary transition-colors" data-testid="footer-labels-dir-link">{t("footer.labels.dir")}</Link></li>
            <li><Link to="/contact" className="hover:text-primary transition-colors" data-testid="footer-contact-link">{t("nav.contact")}</Link></li>
          </ul>
        </div>
        <div>
          <div className="eyebrow mb-4">{t("nav.legal")}</div>
          <ul className="space-y-2.5">
            {PILLARS.map(([key, map]) => (
              <li key={key}>
                <Link to={pagePath(key, lang)} className="hover:text-primary transition-colors" data-testid={`footer-pillar-${key}`}>{label(map)}</Link>
              </li>
            ))}
          </ul>
          <div className="eyebrow mt-6 mb-3">{t("modes.eyebrow")}</div>
          <ul className="space-y-2 text-muted-foreground">
            {SECTORS.map(([key, map]) => (
              <li key={key}>
                <Link to={pagePath(key, lang)} className="hover:text-primary transition-colors" data-testid={`footer-sector-${key}`}>{label(map)}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="eyebrow mb-4">{t("footer.legal")}</div>
          <ul className="space-y-2.5 text-muted-foreground">
            <li><Link to="/cgv" className="hover:text-primary transition-colors" data-testid="footer-cgv-link">CGV & CGU</Link></li>
            <li><Link to="/mentions-legales" className="hover:text-primary transition-colors" data-testid="footer-mentions-link">Mentions légales</Link></li>
            <li><button type="button" onClick={() => window.dispatchEvent(new Event("empco:open-cookies"))} className="hover:text-primary transition-colors underline-offset-2 hover:underline" data-testid="footer-cookies-link">{t("cookie.footer.link")}</button></li>
            <li>contact@empco-compliance.eu</li>
          </ul>

          <div className="eyebrow mt-6 mb-3">Nous suivre</div>
          <ul className="space-y-2.5 text-muted-foreground">
            <li>
              <a href="https://www.linkedin.com/company/146606902/" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors inline-flex items-center gap-2" data-testid="footer-linkedin-link">
                <Linkedin className="w-3.5 h-3.5" /> LinkedIn EmpCo Compliance
              </a>
            </li>
            <li>
              <a href="https://www.youtube.com/@EMPCOCOMPLIANCE" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors inline-flex items-center gap-2" data-testid="footer-youtube-link">
                <Youtube className="w-3.5 h-3.5" /> YouTube EmpCo Compliance
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border bg-muted/50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
          <p className="font-mono text-[11px] text-muted-foreground">© 2026 EmpCo SAS · v1.0</p>
          <p className="font-mono text-[11px] text-muted-foreground">{t("footer.hosting")}</p>
        </div>
      </div>
    </footer>
  );
}
